import { Header, Table } from '@tanstack/react-table';

import { ChevronDownSmallFilledIcon } from '@/ui/Icons/ChevronDownSmallFilledIcon';

import { classNames } from '../../../../utils/classnames';

type Props<TData> = {
  title: string | React.ReactNode;
  property: string;
  sortable?: boolean;
  table: Table<TData>;
  header: Header<TData, unknown>;
};

const getSortDirection = <TData,>(table: Table<TData>, header: Header<TData, unknown>) => {
  const { sorting } = table.getState();
  const sortEntry = sorting.find(({ id }) => id === header.id);

  if (!sortEntry) {
    return undefined;
  }

  return sortEntry.desc ? 'desc' : 'asc';
};

export function HeaderCell<TData>({ title, property, sortable, table, header }: Props<TData>) {
  const sortDirection = getSortDirection(table, header);

  const handleSort = () => table.setSorting([{ id: property, desc: sortDirection !== 'desc' }]);

  const content = (
    <>
      {title}
      {sortable && (
        <ChevronDownSmallFilledIcon
          className={classNames(
            'size-3',
            sortDirection === 'asc' ? 'rotate-180' : '',
            sortDirection !== undefined ? 'text-token-gray-1600' : 'text-transparent',
          )}
        />
      )}
    </>
  );

  if (!sortable) {
    return <span className="flex items-center gap-0.5">{content}</span>;
  }

  return (
    <button
      type="button"
      className={classNames(
        'flex items-center group gap-0.5 bg-transparent border-0 p-0 text-left text-inherit',
        'cursor-pointer rounded-xs hover:text-token-gray-1600',
        'focus-visible:outline-[3px] focus-visible:outline-token-gray-600 focus-visible:outline-solid',
        { 'text-token-gray-1600': sortDirection !== undefined },
      )}
      onClick={handleSort}
    >
      {content}
    </button>
  );
}
